import { useMemo } from 'react';
import { useWealth } from '../../api';
import { chartAssetColors, chartLiabilityColors } from '../../components/wealth/charts/pieChartOptions';
import type { IMap } from '../../interfaces/generic';
import type { IWealthChartData } from '../../interfaces/wealth';

export const useWealthChartData = () => {
    const wealthData = useWealth();
    const wealthChartDataMap: IMap<IWealthChartData | undefined> = useMemo(
        () =>
            !wealthData
                ? {}
                : {
                      ASSET: {
                          balanceAccount: wealthData.assets,
                          visibleCategories: wealthData.assets?.categories.filter((category) => category.totalAmount) ?? [],
                          totalAmount: wealthData.assetsTotal,
                          chartColors: chartAssetColors,
                          titleTranslationKey: 'wealthChartAssetsTabTitle',
                          netAmount: wealthData.liabilitiesTotal - wealthData.assetsTotal,
                          netRatio: (wealthData.liabilitiesTotal - wealthData.assetsTotal) / wealthData.liabilitiesTotal,
                          netTitleTranslationKey: 'wealthNetLiabilities',
                      },
                      LIABILITY: {
                          balanceAccount: wealthData.liabilities,
                          visibleCategories: wealthData.liabilities?.categories.filter((category) => category.totalAmount) ?? [],
                          totalAmount: wealthData.liabilitiesTotal,
                          chartColors: chartLiabilityColors,
                          titleTranslationKey: 'wealthChartLiabilitiesTabTitle',
                          netAmount: wealthData.assetsTotal - wealthData.liabilitiesTotal,
                          netRatio: (wealthData.assetsTotal - wealthData.liabilitiesTotal) / wealthData.assetsTotal,
                          netTitleTranslationKey: 'wealthNetAssets',
                      },
                      INCOME: {
                          balanceAccount: wealthData.income,
                          visibleCategories: wealthData.income?.categories.filter((category) => category.totalAmount) ?? [],
                          totalAmount: wealthData.incomeTotal,
                          chartColors: chartAssetColors,
                          titleTranslationKey: 'wealthIncome',
                          netAmount: wealthData.expensesTotal - wealthData.incomeTotal,
                          netTitleTranslationKey: 'wealthNetExpenses',
                          netRatio: (wealthData.expensesTotal - wealthData.incomeTotal) / wealthData.expensesTotal,
                      },
                      EXPENSE: {
                          balanceAccount: wealthData.expenses,
                          visibleCategories: wealthData.expenses?.categories.filter((category) => category.totalAmount) ?? [],
                          totalAmount: wealthData.expensesTotal,
                          chartColors: chartLiabilityColors,
                          titleTranslationKey: 'wealthExpenses',
                          netAmount: wealthData.incomeTotal - wealthData.expensesTotal,
                          netTitleTranslationKey: 'wealthNetIncome',
                          netRatio: (wealthData.incomeTotal - wealthData.expensesTotal) / wealthData.incomeTotal,
                      },
                  },
        [wealthData],
    );

    return wealthChartDataMap;
};
